import React, { useMemo, useEffect, useRef, useContext, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { installViewer } from '@evodicom/ohif-viewer';
import { useQueryClient } from '@tanstack/react-query';
import html2canvas from 'html2canvas';
import { Box } from '@mui/material';
import If from '@components/If';
import SoftLoading from '@components/SoftLoading';
import useConfirm from '@core/hooks/useConfirm';
import useGeneratedReports, { GENERATED_REPORTS_QUERY_KEY } from '@core/hooks/useGeneratedReports';
import useProfile from '@core/hooks/useProfile';
import useSnackbar from '@core/hooks/useSnackbar';
import useViewportSize from '@core/hooks/useViewportSize';
import { ThemeContext } from '@core/providers/ThemeProvider';
import ViewerInfoCommand from '@core/types/ViewerInfoCommand';
import { hideStartLoader } from '@core/utils/startLoader';
import Form from 'app/features/reports/add/components/Form';
import {
  ReportsSource,
  ReportsViewState,
  StudyReportItem,
  useStudyReports,
} from 'app/features/reports/view';
import getPrintableReport from '../../api/getPrintableReport';
import useGetReportsCreatedByUser from '../../hooks/useGetReportsCreatedByUser';
import useStudyExportDownload from '../../hooks/useStudyExportDownload';
import useViewerSideReportFunctionality from '../../hooks/useViewerSideReportFunctionality';
import invalidateStudyReportQueries from '../../utils/invalidateStudyReportQueries';
import ReportsOverlay from '../ReportsOverlay';
import ViewerTabBar from '../ViewerTabBar/ViewerTabBar';
import DownloadProgressOverlay from './DownloadProgressOverlay';
import config from './config';
import deleteSeriesHandler from './deleteSeriesHandler';
import useSx from './sx';

export interface CommandArguments {
  commandName: ViewerInfoCommand;
}

interface PdfLoadedEventData {
  pdfUrl: string;
  sopInstanceUID: string;
}

interface ReportEventData {
  studyId: string;
  seriesId: string;
  objectId: string;
}

export interface ViewerProps {
  studyInstanceUid: string;
  readOnly?: boolean;
  isAnon: boolean;
  isAdministrator: boolean;
  baseUrl: string;
  onCommandClick?: (commandArgs: CommandArguments) => void;
  /**
   * The study's PDF reports, over the images, switched with the bottom tab
   * bar. 'shared': share-link viewer (only print / download).
   */
  reports?: { source: ReportsSource; view: ReportsViewState; variant: 'study' | 'shared' };
}

const containerId = 'ohif-viewer';

const Viewer = ({
  studyInstanceUid,
  readOnly,
  baseUrl,
  isAnon,
  isAdministrator,
  onCommandClick,
  reports,
}: ViewerProps) => {
  const showSnackbar = useSnackbar();
  const {
    phase: downloadPhase,
    progress: downloadProgress,
    start: startStudyDownload,
  } = useStudyExportDownload(studyInstanceUid);
  const { role } = useProfile();
  const { isConfirmed } = useConfirm();
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const { organizationSettings, dicomServiceTokenSettings } = useContext(ThemeContext);

  const dicomWebUrl = organizationSettings?.DicomWebServer || '';
  const loadedReportDataUrl = useRef<PdfLoadedEventData | null>(null);
  const {
    reportData,
    isError,
    isLoading: isLoadingGeneratedReports,
    error,
  } = useGeneratedReports(studyInstanceUid);
  const { reportIds: reportsCreatedByUserIds, isLoading: isLoadingReportsCreatedByUser } =
    useGetReportsCreatedByUser(studyInstanceUid);
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();
  const [hasViewerInitialized, setHasViewerInitialized] = useState(false);
  const viewerRef = useRef<HTMLDivElement>(null);
  const reportRef = useRef<HTMLDivElement>(null);
  const {
    showSideReport,
    handleOpenSideReport,
    handleUploadReport,
    handleCancelSideReport,
    isUploading,
    requireDoubleReading,
  } = useViewerSideReportFunctionality({
    studyInstanceUid,
    viewerRef,
    reportRef,
  });
  const sx = useSx({ showSideReport });

  const viewerConfig = useMemo(
    () =>
      config({
        studyInstanceUid,
        isAnon,
        isAdministrator,
        role,
        routerBasename: baseUrl,
        deleteSeriesHandler: (displaySet, existingSeriesCount) =>
          deleteSeriesHandler(
            displaySet,
            existingSeriesCount,
            (message) => showSnackbar({ title: 'Eliminado', message: message }),
            isConfirmed,
          ),
        reportsCreatedByUser: reportsCreatedByUserIds || [],
        hasPendingOrDraftReport: reportData?.hasPendingOrDraft,
        generatedReportIds: isError || readOnly ? [] : reportData?.reportIds || [],
      }),
    [
      studyInstanceUid,
      readOnly,
      isAnon,
      baseUrl,
      showSnackbar,
      role,
      isError,
      isConfirmed,
      reportData,
      dicomWebUrl,
      reportsCreatedByUserIds,
    ],
  );

  const printPhoto = useCallback(
    async ($e: Event) => {
      setIsActionInProgress(true);
      const printPhotoEvent = $e as CustomEvent<{ activeViewportIndex: number }>;
      // Obtener el elemento del DOM que se desea capturar
      const elements = document.querySelectorAll('.viewport-wrapper');
      const elemento = elements[printPhotoEvent.detail.activeViewportIndex] as HTMLElement;

      const canvas = await html2canvas(elemento);

      canvas.toBlob(async function (blob) {
        navigator.clipboard
          .write([
            new ClipboardItem({
              'image/png': blob!,
            }),
          ])
          .then(function () {
            showSnackbar({
              title: 'Imagen copiada exitosamente.',
              message: 'La captura se ha copiado al portapapeles correctamente.',
              type: 'success',
            });
          })
          .catch(function () {
            showSnackbar({
              title: 'Error al copiar imagen',
              message: 'Ha ocurrido un error al intentar copiar la captura al portapapeles.',
              type: 'error',
            });
          });
      }, 'image/png');
      setIsActionInProgress(false);
    },
    [showSnackbar],
  );

  const handleCommandClick = useCallback(
    ($e: Event) => {
      const { detail } = $e as CustomEvent<CommandArguments>;
      if (detail.commandName === 'download') {
        void startStudyDownload();
        return;
      }
      onCommandClick?.(detail);
    },
    [onCommandClick, startStudyDownload],
  );

  useEffect(() => {
    if (isLoadingGeneratedReports && isLoadingReportsCreatedByUser && !readOnly) {
      return;
    }

    if (isError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error while loading generated report IDs', error);
      }
    }

    if (!dicomWebUrl) {
      console.error('DICOM Web URL is not configured correctly.');
      return;
    }

    const updatedViewerConfig = {
      ...viewerConfig,
      servers: {
        dicomWeb: viewerConfig.servers.dicomWeb.map((server) => ({
          ...server,
          wadoRoot: dicomWebUrl,
          qidoRoot: dicomWebUrl,
          wadoUriRoot: dicomWebUrl,
          delowRoot: dicomWebUrl,
        })),
      },
    };

    const handleReportUpload = (event: Event) => {
      event.preventDefault();
      // TODO: report back the report to OHIF viewer
      window.location.reload();
    };

    const handleToolbarResize = ($e: Event) => {
      const { detail } = $e as CustomEvent<ResizeObserverEntry>;
      const [{ blockSize } = { blockSize: 0 }] = detail.borderBoxSize;
      document.documentElement.style.setProperty('--ohif-toolbar-height', `${blockSize ?? '78'}px`); // 78px is the default
    };

    const handlePdfLoaded = ($e: Event) => {
      const pdfLoadedEvent = $e as CustomEvent<PdfLoadedEventData | null>;
      loadedReportDataUrl.current = pdfLoadedEvent.detail;
    };

    const handleReportDownload = ($e: Event) => {
      if (!loadedReportDataUrl.current) {
        return;
      }

      const { pdfUrl, sopInstanceUID } = loadedReportDataUrl.current;
      const reportEvent = $e as CustomEvent<ReportEventData>;
      if (reportEvent.detail.objectId !== sopInstanceUID) {
        return;
      }

      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = pdfUrl.substr(pdfUrl.lastIndexOf('/') + 1);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    const handleReportPrint = ($e: Event) => {
      if (!loadedReportDataUrl.current) {
        return;
      }

      const { pdfUrl, sopInstanceUID } = loadedReportDataUrl.current;
      const reportEvent = $e as CustomEvent<ReportEventData>;
      if (reportEvent.detail.objectId !== sopInstanceUID) {
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow!.focus();
        iframe.contentWindow!.print();
      };

      window.onfocus = () => {
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.onfocus = null;
        }, 100);
      };
    };

    const handleReportPrintNoTemplate = async ($e: Event) => {
      setIsActionInProgress(true);
      if (!loadedReportDataUrl.current) {
        return;
      }

      const { sopInstanceUID } = loadedReportDataUrl.current;
      const reportEvent = $e as CustomEvent<ReportEventData>;
      if (reportEvent.detail.objectId !== sopInstanceUID) {
        return;
      }
      // todo
      const { studyId, objectId } = reportEvent.detail;
      try {
        const report = await getPrintableReport(studyId, objectId);
        const reportUrl = URL.createObjectURL(report);
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = reportUrl;
        document.body.appendChild(iframe);

        iframe.onload = () => {
          iframe.contentWindow!.focus();
          iframe.contentWindow!.print();
        };

        window.onfocus = () => {
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.onfocus = null;
            URL.revokeObjectURL(reportUrl);
          }, 100);
        };
      } catch (testError: any) {
        console.log({ testError });
      } finally {
        setIsActionInProgress(false);
      }
    };

    const handleReportAdded = async () => {
      // invalidate queries associated with reports
      await queryClient.invalidateQueries([GENERATED_REPORTS_QUERY_KEY]);
    };

    const hasLoadedDataForViewer =
      dicomServiceTokenSettings?.token &&
      !isLoadingReportsCreatedByUser &&
      !isLoadingGeneratedReports;

    const hasLoadedDataForReadOnlyViewer = dicomServiceTokenSettings?.token && readOnly;

    if (hasLoadedDataForViewer || hasLoadedDataForReadOnlyViewer) {
      installViewer(updatedViewerConfig, containerId, () => {
        setHasViewerInitialized(true);
        document.addEventListener('evodicom.command.click', handleCommandClick);
        document.addEventListener('evodicom.toolbar.resize', handleToolbarResize);
        document.addEventListener('evodicom.report.upload', handleReportUpload);
        document.addEventListener('evodicom.pdf.loaded', handlePdfLoaded);
        document.addEventListener('evodicom.report.download', handleReportDownload);
        document.addEventListener('evodicom.report.print', handleReportPrint);
        document.addEventListener('evodicom.report.printNoTemplate', handleReportPrintNoTemplate);
        document.addEventListener('evodicom.report.added', handleReportAdded);
        document.addEventListener('evodicom.handleSideReport', handleOpenSideReport);
        document.addEventListener('evodicom.printPhoto', printPhoto);
        document.addEventListener('evodicom.changeLoadingState', async ($e: Event) => {
          const loadingEvent = $e as CustomEvent<{ loading: boolean }>;
          setIsActionInProgress(loadingEvent.detail.loading);
        });
      });
    }

    return () => {
      document.removeEventListener('evodicom.command.click', handleCommandClick);
      document.removeEventListener('evodicom.toolbar.resize', handleToolbarResize);
      document.removeEventListener('evodicom.report.upload', handleReportUpload);
      document.removeEventListener('evodicom.pdf.loaded', handlePdfLoaded);
      document.removeEventListener('evodicom.report.download', handleReportDownload);
      document.removeEventListener('evodicom.report.print', handleReportPrint);
      document.removeEventListener('evodicom.report.printNoTemplate', handleReportPrintNoTemplate);
      document.removeEventListener('evodicom.report.added', handleReportAdded);
      document.removeEventListener('evodicom.handleSideReport', handleOpenSideReport);
      document.removeEventListener('evodicom.printPhoto', printPhoto);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    onCommandClick,
    viewerConfig,
    isLoadingGeneratedReports,
    isError,
    dicomWebUrl,
    dicomServiceTokenSettings,
    printPhoto,
  ]);

  // The section param, not every search param: switching between images and
  // reports must not reopen the section's dialog.
  const section = searchParams.get('section');
  useEffect(() => {
    if (hasViewerInitialized) hideStartLoader({ isAuthenticated: true });
    if (hasViewerInitialized && section && !readOnly) {
      onCommandClick?.({ commandName: section as ViewerInfoCommand });
    }
  }, [hasViewerInitialized, onCommandClick, readOnly, section]);

  const { isUpMedium } = useViewportSize();
  const { reports: studyReports, isSuccess: reportsLoaded } = useStudyReports(reports?.source);
  const reportsView = reports?.view;

  const handleReportCommand = useCallback(
    (commandName: ViewerInfoCommand) => onCommandClick?.({ commandName }),
    [onCommandClick],
  );

  // Reports that also live in the DICOM study (legacy) are a series of the
  // images viewer, which can't drop one: reload the page (the URL keeps the
  // reports view open).
  const refreshAfterReportChange = useCallback(
    async (report: StudyReportItem) => {
      if (report.source === 'Dicom' || report.seriesInstanceUid) {
        window.location.reload();
        return;
      }
      await invalidateStudyReportQueries(queryClient);
    },
    [queryClient],
  );

  // Same editor tab as the toolbar's "Editar Reporte"
  // (ohif/extensions/evodicom/commandsModule.js), for the report shown.
  const handleEditReport = useCallback(
    (report: StudyReportItem) => {
      const params = new URLSearchParams({
        studyUid: studyInstanceUid,
        SOPInstanceUID: report.sopInstanceUid,
      });
      if (report.seriesInstanceUid) {
        params.set('SeriesInstanceUID', report.seriesInstanceUid);
      }
      const reportWindow = window.open(`${window.location.origin}/reports?${params}`, '_blank');
      const editorWindow = window as Window & {
        onSuccessResponse?: () => void;
        onCancelReport?: () => void;
      };
      editorWindow.onSuccessResponse = () => {
        reportWindow?.close();
        void refreshAfterReportChange(report);
      };
      editorWindow.onCancelReport = () => reportWindow?.close();
    },
    [studyInstanceUid, refreshAfterReportChange],
  );

  return (
    <>
      <Box id="viewer-container" sx={sx.root} display="flex">
        {/* The images viewer mounts into #ohif-viewer (outside React), so the
            report viewer goes next to it, over it. */}
        <Box
          ref={viewerRef}
          position="relative"
          className="viewer"
          // What #ohif-viewer's own `width: 100%` did when it was the row's item.
          width="100%"
        >
          <Box id={containerId} />
          {reports && reportsView?.isOpen && (
            <ReportsOverlay
              source={reports.source}
              selectedSop={reportsView.selectedSop}
              onSelect={reportsView.select}
              mobile={!isUpMedium}
              variant={reports.variant}
              studyInstanceUid={studyInstanceUid}
              readOnly={!!readOnly}
              isAnon={isAnon}
              isAdministrator={isAdministrator}
              onCommand={handleReportCommand}
              onEditReport={handleEditReport}
              onReportDeleted={refreshAfterReportChange}
              setBusy={setIsActionInProgress}
            />
          )}
        </Box>
        <If condition={showSideReport}>
          <Box
            minWidth="40%"
            width="40%"
            id="report"
            maxHeight="calc(100vh - 64px - var(--evodicom-tabbar-height, 0px))"
            overflow="auto"
            paddingLeft="4px"
            paddingY="20px"
            ref={reportRef}
          >
            <Form
              handleReportSubmit={handleUploadReport}
              initForm={{}}
              handleCancel={handleCancelSideReport}
              isLoading={isUploading}
              studyId={studyInstanceUid}
              smallView
              requireDoubleReading={requireDoubleReading}
              showDoubleReadingOption={!requireDoubleReading}
            />
          </Box>
        </If>

        <SoftLoading isOpen={isActionInProgress} />
        <DownloadProgressOverlay phase={downloadPhase} progress={downloadProgress} />
      </Box>
      {reportsView && (
        <ViewerTabBar
          active={reportsView.isOpen ? 'reports' : 'images'}
          onChange={(tab) => (tab === 'reports' ? reportsView.open() : reportsView.close())}
          // Enabled while loading so a link that opens the reports never shows it greyed out.
          reportsEnabled={!reportsLoaded || studyReports.length > 0}
        />
      )}
    </>
  );
};

export default Viewer;
